import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Box, Grid, Paper, Typography, TextField, Button, CircularProgress, Divider, Chip as MuiChip, Switch, FormControlLabel } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { Row } from "simple-flexbox";
import { useSelector } from "react-redux";
import jwt_decode from "jwt-decode";
import ChipToken, { getChipColor } from "../../components/ChipToken";

import { GameTable } from "./table.js";
import SelectTextFields from "../../components/customDropDown/dropDown";
import CustomizedButtons from "../../components/button/button";

import { axiosRequest } from "../../utility/axios";
import { apiBaseUrl } from "../../constant/endpoint";
import { transcationArrayType, transcationMode, walletFilterOptions } from "../../constant/constant";
import { parseGameTranscation } from "../../helper";

const defaultDateTimeState = {
  date_from: "",
  date_to: "",
};

const defaultTransactionType = {
  value: "All",
  label: "All",
  _id: 0,
};

export default function Transaction() {
  const [dateTime, setDateTime] = useState(defaultDateTimeState);
  const [totalAmt, setTotalAmt] = useState(0);
  const [totalCashbackAmt, setTotalCashbackAmt] = useState(0);
  const [gameTranscation, setGameTranscation] = useState([]);
  const [transactionType, setTransactionType] = useState(defaultTransactionType);
  const bankModeDefault = { value: "BANK", label: "BANK", _id: 1 };
  const [transactionMode, setTransactionMode] = useState(defaultTransactionType);
  const [walletFilter, setWalletFilter] = useState(defaultTransactionType);
  const [transcation, setTranscation] = useState({});
  const [disableOtherFilters, setDisableOtherFilters] = useState(false);
  const [phoneOrPlayerId, setPhoneOrPlayerId] = useState("");
  const [offerApprovalFilter, setOfferApprovalFilter] = useState({ value: "All", _id: 0 });
  const [cashbackFilter, setCashbackFilter] = useState({ value: "All", _id: 0 });
  const [hasInvoiceFilter, setHasInvoiceFilter] = useState(false);

  const offerApprovalOptions = [
    { value: "All", label: "All", _id: 0 },
    { value: "PENDING", label: "Pending Approval", _id: 1 },
    { value: "APPROVED", label: "Approved", _id: 2 },
    { value: "REJECTED", label: "Rejected", _id: 3 },
    { value: "AUTO", label: "Auto (No Approval)", _id: 4 },
  ];

  const [exporting, setExporting] = useState(false);

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPage: 1,
    size: 20,
  });
  const [searched, setSearched] = useState(false);

  const [counterSummary, setCounterSummary] = useState(null);
  const [showOtherDeposits, setShowOtherDeposits] = useState(false);

  // Gmail-style "g" then "i" key sequence reveals OTHER-type deposit transactions
  useEffect(() => {
    let lastKey = "";
    let lastKeyTime = 0;
    const handleKeyDown = (e) => {
      const tag = e.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || e.target?.isContentEditable) return;
      const now = Date.now();
      if (e.key.toLowerCase() === "i" && lastKey === "g" && now - lastKeyTime < 1000) {
        setShowOtherDeposits((prev) => !prev);
        lastKey = "";
        return;
      }
      lastKey = e.key.toLowerCase();
      lastKeyTime = now;
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const visibleTranscation = useMemo(
    () => showOtherDeposits
      ? gameTranscation
      : gameTranscation.filter((t) => !(t.txn_type === "DEPOSIT" && t.payment_mode === "OTHER")),
    [gameTranscation, showOtherDeposits]
  );

  const loginSlice = useSelector((state) => state.loginSlice);
  const token = loginSlice.token;
  const accessLevel = useMemo(() => {
    try { return token ? jwt_decode(token)?.accessLevel : 0; } catch { return 0; }
  }, [token]);
  const isBankOnly = accessLevel === 999;

  const fetchCounterSummary = useCallback(async () => {
    try {
      const res = await axiosRequest("GET", "/shift-management/current-summary", token);
      if (res?.status === "success") setCounterSummary(res.data);
    } catch { /* silent */ }
  }, [token]);

  useEffect(() => {
    fetchCounterSummary();
    const interval = setInterval(fetchCounterSummary, 30000);
    return () => clearInterval(interval);
  }, [fetchCounterSummary]);

  const getGameTranscation = async (page = 1, size = 20, refreshData) => {
    let postData = await parseGameTranscation(
      transcation,
      transactionType,
      dateTime,
      page,
      size,
      transactionMode,
      walletFilter,
    );
    if (offerApprovalFilter?.value && offerApprovalFilter.value !== "All") {
      postData.offer_approval_status = offerApprovalFilter.value;
    }
    if (cashbackFilter?.value === "HAS_CASHBACK") {
      postData.has_cashback = true;
    }
    if (transactionType?.value === "DEPOSIT" && hasInvoiceFilter) {
      postData.has_invoice = true;
    }

    if (refreshData) postData = refreshData;

    const url = `${apiBaseUrl}/transaction`;
        console.log("postData",postData);
    const res = await axiosRequest("POST", url, token, postData);

    if (res?.status === "success") {
      setGameTranscation(res.data.transactionList);
      setPagination(res.data.pagniationData);
      setTotalAmt(res.data.totalTnxAmt || 0);
      setTotalCashbackAmt(res.data.totalCashbackAmt || 0);
      setSearched(true);
    }
  };

  useEffect(() => {
    if (isBankOnly) setTransactionMode(bankModeDefault);
    getGameTranscation();
  }, [isBankOnly]);

  const handleSubmit = () => {
    getGameTranscation();
  };

  const handleReset = () => {
    setDateTime(defaultDateTimeState);
    setTranscation({});
    setTransactionType(defaultTransactionType);
    setTransactionMode(isBankOnly ? bankModeDefault : defaultTransactionType);
    setWalletFilter(defaultTransactionType);
    setOfferApprovalFilter({ value: "All", _id: 0 });
    setCashbackFilter({ value: "All", _id: 0 });
    setHasInvoiceFilter(false);
    setDisableOtherFilters(false);
    setSearched(false);
    setTotalAmt(0);
    setTotalCashbackAmt(0);
    setPhoneOrPlayerId("");
    getGameTranscation(1, 20, defaultDateTimeState);
  };

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      let postData = await parseGameTranscation(transcation, transactionType, dateTime, 1, 99999, transactionMode, walletFilter);
      if (offerApprovalFilter?.value && offerApprovalFilter.value !== "All") {
        postData.offer_approval_status = offerApprovalFilter.value;
      }
      if (cashbackFilter?.value === "HAS_CASHBACK") {
        postData.has_cashback = true;
      }
      if (transactionType?.value === "DEPOSIT" && hasInvoiceFilter) {
        postData.has_invoice = true;
      }
      const res = await fetch(`${apiBaseUrl}/transaction/export-csv`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "token": token },
        body: JSON.stringify(postData),
      });
      if (!res.ok) { console.error("Export failed", res.status); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `transactions_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Export failed", e);
    } finally {
      setExporting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setTranscation((prev) => {
      const updated = { ...prev };
      delete updated.phone;
      delete updated.player_id;

      if (name === "phone_or_player_id") {
        setPhoneOrPlayerId(value);
        const trimmed = value.trim();
        if (trimmed) {
          if (/^\d{10}$/.test(trimmed)) updated.phone = trimmed;
          else updated.player_id = trimmed;
        }
      } else {
        if (value.trim()) updated[name] = value.trim();
        else delete updated[name];
      }

      setDisableOtherFilters(Object.keys(updated).length > 0);

      return updated;
    });
  };

  const setDate = (date, key) => {
    setDateTime((prev) => ({ ...prev, [key]: date }));
  };

  const handleTransactionType = (label, value, _id = 1) => {
    setTransactionType({ value, _id });
  };

  const handleTransactionMode = (label, value, _id = 1) => {
    setTransactionMode({ value, _id });
  };

  return (
    <Box sx={{ width: "100%", marginTop: { xs: "55px", md: "95px" }, pb: 6 }}>

      {/* ── AVAILABLE ON COUNTER ── */}
      {counterSummary && (
        <Paper sx={{ p: { xs: 2, md: 3 }, mb: 2, borderRadius: 3, background: "linear-gradient(135deg,#0f2027,#203a43,#2c5364)", color: "#fff" }}>
          <Typography variant="subtitle1" fontWeight={700} mb={1.5} sx={{ opacity: 0.9 }}>
            Available on Counter
          </Typography>
          <Grid container spacing={2} alignItems="flex-start">
            {/* Cash */}
            <Grid item xs={6} sm={3} md={2}>
              <Box sx={{ background: "rgba(255,255,255,0.1)", borderRadius: 2, p: 1.5, textAlign: "center" }}>
                <Typography variant="caption" sx={{ opacity: 0.75 }}>Cash</Typography>
                <Typography variant="h6" fontWeight={800} sx={{ color: "#69f0ae" }}>
                  {Number(counterSummary.counter_cash?.total || 0).toLocaleString("en-IN")}
                </Typography>
              </Box>
            </Grid>
            {/* Bank */}
            <Grid item xs={6} sm={3} md={2}>
              <Box sx={{ background: "rgba(255,255,255,0.1)", borderRadius: 2, p: 1.5, textAlign: "center" }}>
                <Typography variant="caption" sx={{ opacity: 0.75 }}>Bank</Typography>
                <Typography variant="h6" fontWeight={800} sx={{ color: "#ffcc80" }}>
                  {Number(counterSummary.counter_bank?.total || 0).toLocaleString("en-IN")}
                </Typography>
              </Box>
            </Grid>
            {/* Chips total */}
            <Grid item xs={6} sm={3} md={2}>
              <Box sx={{ background: "rgba(255,255,255,0.1)", borderRadius: 2, p: 1.5, textAlign: "center" }}>
                <Typography variant="caption" sx={{ opacity: 0.75 }}>Chips</Typography>
                <Typography variant="h6" fontWeight={800} sx={{ color: "#40c4ff" }}>
                  {Number(counterSummary.counter_chips?.total || 0).toLocaleString("en-IN")}
                </Typography>
              </Box>
            </Grid>
            {/* Chip denominations */}
            {counterSummary.counter_chips?.denominations && Object.keys(counterSummary.counter_chips.denominations).length > 0 && (
              <Grid item xs={12} sm={6} md={8}>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {Object.entries(counterSummary.counter_chips.denominations)
                    .sort((a, b) => Number(a[0]) - Number(b[0]))
                    .map(([d, q]) => {
                      const cc = getChipColor(d);
                      return (
                        <Box key={d} sx={{ px: 1.5, py: 0.8, borderRadius: 1.5, background: cc.bg, textAlign: "center", minWidth: 72, display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
                          <ChipToken denomination={d} size={36} />
                          <Typography variant="caption" sx={{ color: cc.text, opacity: 0.85, display: "block" }}>{d}</Typography>
                          <Typography variant="body2" fontWeight={700} sx={{ color: cc.text }}>{q} pcs</Typography>
                        </Box>
                      );
                    })}
                </Box>
              </Grid>
            )}
            {/* Promo chips total */}
            {counterSummary.counter_promo_chips?.total > 0 && (
              <Grid item xs={6} sm={3} md={2}>
                <Box sx={{ background: "rgba(123,31,162,0.25)", borderRadius: 2, p: 1.5, textAlign: "center" }}>
                  <Typography variant="caption" sx={{ opacity: 0.75 }}>Promo Chips</Typography>
                  <Typography variant="h6" fontWeight={800} sx={{ color: "#ce93d8" }}>
                    {Number(counterSummary.counter_promo_chips?.total || 0).toLocaleString("en-IN")}
                  </Typography>
                </Box>
              </Grid>
            )}
            {/* Promo chip denominations */}
            {counterSummary.counter_promo_chips?.denominations && Object.keys(counterSummary.counter_promo_chips.denominations).length > 0 && (
              <Grid item xs={12} sm={6} md={8}>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {Object.entries(counterSummary.counter_promo_chips.denominations)
                    .sort((a, b) => Number(a[0]) - Number(b[0]))
                    .map(([d, q]) => (
                      <Box key={`P${d}`} sx={{ px: 1.5, py: 0.8, borderRadius: 1.5, background: "#7b1fa2", textAlign: "center", minWidth: 72 }}>
                        <Typography variant="caption" sx={{ color: "#fff", opacity: 0.85, display: "block" }}>{d} PROMO</Typography>
                        <Typography variant="body2" fontWeight={700} sx={{ color: "#fff" }}>{q} pcs</Typography>
                      </Box>
                    ))}
                </Box>
              </Grid>
            )}
          </Grid>
        </Paper>
      )}

      <Paper sx={{ p: { xs: 2, md: 4 } }}>
        <Box display="flex" justifyContent="center" alignItems="center" gap={2} mb={3}>
          <Typography variant="h5">User Transaction</Typography>
          {isBankOnly && (
            <MuiChip label="Bank Mode Only" color="info" size="small" sx={{ fontWeight: 700 }} />
          )}
        </Box>

        <Grid container spacing={3}>
          <Grid item lg={3} xs={12}>
            <TextField
              size="small"
              fullWidth
              label="Transaction Id"
              name="transaction_id"
              value={transcation?.transaction_id || ""}
              onChange={handleChange}
              disabled={disableOtherFilters && !transcation?.transaction_id}
            />
          </Grid>

          <Grid item lg={3} xs={12}>
            <TextField
              size="small"
              fullWidth
              label="Phone / Player ID"
              name="phone_or_player_id"
              value={phoneOrPlayerId}
              onChange={handleChange}
              disabled={disableOtherFilters && !transcation?.phone && !transcation?.player_id}
              helperText="10-digit number = phone; otherwise player ID"
            />
          </Grid>

          <Grid item lg={3} xs={12}>
            <TextField
              size="small"
              fullWidth
              label="Player Name"
              name="name"
              value={transcation?.name || ""}
              onChange={handleChange}
              disabled={disableOtherFilters && !transcation?.name}
            />
          </Grid>

          <Grid item lg={3} xs={12}>
            <TextField
              size="small"
              fullWidth
              label="User Email"
              name="user_email"
              value={transcation?.user_email || ""}
              onChange={handleChange}
              disabled={disableOtherFilters && !transcation?.user_email}
            />
          </Grid>

          <Grid item lg={3} xs={12}>
            <SelectTextFields
              label="Transaction Type"
              value={transactionType?.value}
              handleOnChange={handleTransactionType}
              list={transcationArrayType}
            />
          </Grid>
          <Grid item lg={3} xs={12}>
            <SelectTextFields
              label="Transaction Mode"
              value={transactionMode?.value}
              handleOnChange={isBankOnly ? undefined : handleTransactionMode}
              list={isBankOnly ? [bankModeDefault] : transcationMode}
              disabled={isBankOnly}
            />
          </Grid>

          <Grid item lg={3} xs={12}>
            <SelectTextFields
              label="Offer Approval Status"
              value={offerApprovalFilter?.value}
              handleOnChange={(label, value, _id) => setOfferApprovalFilter({ value, _id })}
              list={offerApprovalOptions}
            />
          </Grid>

          <Grid item lg={3} xs={12}>
            <SelectTextFields
              label="Deposit Offer Cashback"
              value={cashbackFilter?.value}
              handleOnChange={(label, value, _id) => setCashbackFilter({ value, _id })}
              list={[
                { value: "All",          label: "All",               _id: 0 },
                { value: "HAS_CASHBACK", label: "Has Cashback Only", _id: 1 },
              ]}
            />
          </Grid>

          <Grid item lg={3} xs={12}>
            <SelectTextFields
              label="Wallet Deducted"
              value={walletFilter?.value}
              handleOnChange={(label, value, _id) => setWalletFilter({ value, _id })}
              list={walletFilterOptions}
            />
          </Grid>

          {transactionType?.value === "DEPOSIT" && (
            <Grid item lg={3} xs={12} display="flex" alignItems="center">
              <FormControlLabel
                control={
                  <Switch
                    checked={hasInvoiceFilter}
                    onChange={(e) => setHasInvoiceFilter(e.target.checked)}
                    color="primary"
                  />
                }
                label="Invoice Generated Only"
              />
            </Grid>
          )}

          <Grid item lg={3} xs={12}>
            <TextField
              size="small"
              fullWidth
              label="Date & Time From"
              type="datetime-local"
              value={dateTime?.date_from || ""}
              onChange={(e) => setDate(e.target.value, "date_from")}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item lg={3} xs={12}>
            <TextField
              size="small"
              fullWidth
              label="Date & Time To"
              type="datetime-local"
              value={dateTime?.date_to || ""}
              onChange={(e) => setDate(e.target.value, "date_to")}
              InputLabelProps={{ shrink: true }}
              disabled={!dateTime?.date_from}
              inputProps={{ min: dateTime?.date_from || "" }}
            />
          </Grid>

          <Grid item xs={12}>
            <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
              <CustomizedButtons
                color="success"
                label="SEARCH"
                variant="contained"
                onClick={handleSubmit}
              />
              <CustomizedButtons
                color="error"
                label="RESET"
                variant="contained"
                onClick={handleReset}
              />
              <Button
                variant="outlined"
                color="primary"
                startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
                onClick={handleExportCsv}
                disabled={exporting}
                sx={{ borderRadius: 2, fontWeight: 600 }}
              >
                {exporting ? "Exporting…" : "Export CSV"}
              </Button>
            </Box>
          </Grid>

          {searched && (
            <Grid item xs={12}>
              <Box display="flex" gap={3} alignItems="center" flexWrap="wrap"
                sx={{ p: 2, borderRadius: 2, background: "linear-gradient(135deg,#1e3c72,#2a5298)", color: "#fff" }}>
                <Box>
                  <Typography variant="caption" sx={{ opacity: 0.75 }}>Total Transactions</Typography>
                  <Typography variant="h6" fontWeight={700}>{pagination.totalRecords ?? gameTranscation.length}</Typography>
                </Box>
                <Box sx={{ borderLeft: "1px solid rgba(255,255,255,0.3)", pl: 3 }}>
                  <Typography variant="caption" sx={{ opacity: 0.75 }}>Total Amount</Typography>
                  <Typography variant="h5" fontWeight={800}>
                    {Number(totalAmt || 0).toLocaleString("en-IN")}
                  </Typography>
                </Box>
                {totalCashbackAmt > 0 && (
                  <Box sx={{ borderLeft: "1px solid rgba(255,255,255,0.3)", pl: 3 }}>
                    <Typography variant="caption" sx={{ opacity: 0.75 }}>Total Cashback</Typography>
                    <Typography variant="h5" fontWeight={800} sx={{ color: "#ffd54f" }}>
                      {Number(totalCashbackAmt).toLocaleString("en-IN")}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
          )}
        </Grid>
      </Paper>

      {searched && visibleTranscation?.length === 0 ? (
        <Paper sx={{ mt: 3, p: 6, textAlign: "center" }}>
          <Typography variant="h6" color="text.secondary">No transactions found</Typography>
          <Typography variant="body2" color="text.disabled" mt={1}>Try adjusting your filters and search again.</Typography>
        </Paper>
      ) : visibleTranscation?.length > 0 ? (
        <Paper sx={{ mt: 3 }}>
          <Row justifyContent="center" style={{ width: "100%" }}>
            <GameTable
              gameTranscation={visibleTranscation}
              pagination={pagination}
              getGameTranscation={getGameTranscation}
            />
          </Row>
        </Paper>
      ) : null}
    </Box>
  );
}
